import { useEffect, useContext, Fragment, lazy, memo, useCallback } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useNavigate } from 'react-router';
import './App.css';
import { AuthLayout, Layout, SuspenseLayout } from './AppRoute';
import { AuthContext } from '../contexts/auth';
import routes from '../constants/routes';
import { useSocket } from '../utils/hooks';
import { isCompanyInit, viewCompany } from '../utils/api/companies';
import { useDispatch } from 'react-redux';
import { setCompanyInit } from '../store/app';
import { useUser } from '../utils/hooks/user';
import { updateAgent } from '../utils/api/user';
import { useEventListener } from 'use-event-listeners';
import { logoutUser } from '../store/auth';
import { toast } from 'react-toastify';
import Forbidden from './Error/Forbidden';
import { withProfiler } from '@sentry/react';
import OnboardingContainer from './Onboarding';
import FeatureRoute from './common/FeatureRoute';
import { useFeatureStore } from '../zustand/features';

const Overview = lazy(() => import('./Overview'));
const Conversations = lazy(() => import('./Conversations'));
const Reports = lazy(() => import('./Reports/Analytics'));
const Agents = lazy(() => import('./Agents'));
const Settings = lazy(() => import('./Settings'));
const Team = lazy(() => import('./Team'));
const Login = lazy(() => import('./Login'));
const Records = lazy(() => import('./Records'));
const Tickets = lazy(() => import('./Tickets'));
const Record = lazy(() => import('./Records/Record'));
const Logs = lazy(() => import('./Reports/Logs'));
const Profile = lazy(() => import('./Profile'));
const ForgotPassword = lazy(() => import('../ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../ResetPassword/ResetPassword'));
const Calendar = lazy(() => import('./Calendar'));
const Setup = lazy(() => import('./Setup'));
const TwoFa = lazy(() => import('./TwoFa'));
const Scheduler = lazy(() => import('./Scheduler'));
const DocumentVerification = lazy(() => import('./DocumentVerification'));
const Esignature = lazy(() => import('./Esignature'));
const DocumentUploadPage = lazy(() => import('./Esignature/DocumentUploadPage'));
const DocumentBuild = lazy(() => import('./Esignature/DocumentBuild'));
const EsignatureTemplates = lazy(() => import('./Esignature/Templates'));
const TemplateUploadPage = lazy(() => import('./Esignature/Templates/TemplateUploadPage'));

// Component to handle all the app initialization logic
function AppInitializer() {
  const { authDispatch } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const user = useUser();

  const getCompanyDetails = async (companyId) => {
    try {
      const cId =
        companyId ||
        user?.company?.id ||
        user?.company_id ||
        (typeof user?.company === 'string' ? user?.company : null);
      if (!cId) return;

      const response = await viewCompany(cId);

      if (response.status === 200) {
        let sessionTimeoutValue = !response?.data?.company?.options?.sessionTimeout
          ? null
          : Number(response?.data?.company?.options?.sessionTimeout) * 1000;

        authDispatch({
          type: 'SESSION_TIMEOUT',
          payload: sessionTimeoutValue
        });

        const company = response?.data?.company;
        const features = company?.features || response?.data?.features;
        const quotas = company?.quotas || response?.data?.quotas;
        if (features) {
          useFeatureStore
            .getState()
            .setFeatures(features, quotas, company?.id || cId, company?.feature_sync || response?.data?.feature_sync);
        }
      }
    } catch (error) {
      useFeatureStore.getState().markSyncUnavailable();
    }
  };

  const checkIsInit = async () => {
    try {
      const {
        data: { status, features, quotas, feature_sync }
      } = await isCompanyInit();
      dispatch(setCompanyInit(status));
      if (features) {
        useFeatureStore.getState().setFeatures(features, quotas, null, feature_sync);
      }
    } catch (error) {
      useFeatureStore.getState().markSyncUnavailable();
      console.log(error);
    }
  };

  const companyIdentifier =
    user?.company?.id ||
    user?.company_id ||
    (typeof user?.company === 'string' ? user?.company : null);

  useEffect(() => {
    if (companyIdentifier) {
      getCompanyDetails(companyIdentifier);
    }
  }, [companyIdentifier]);

  useEffect(() => {
    checkIsInit();
  }, []);

  const updateAgentOnSockketConnect = useCallback(async () => {
    try {
      await updateAgent(user?.id, { is_online: true });
    } catch (error) {
      //
    }
  }, [user, socket.connected]);

  useEffect(() => {
    if (socket.connected && user) {
      updateAgentOnSockketConnect();
    }
  }, [user, socket.connected]);

  const handleNavigation = () => {
    // The access token was rejected, so the authenticated logout endpoint would
    // fail with the same 401. Clear local state and navigate instead.
    dispatch(logoutUser());
    navigate('/auth/login');
    toast.info('session expired', { autoClose: 2000 });
  };

  useEventListener({
    listeners: {
      'log-out-navigation': () => handleNavigation()
    }
  });

  return (
    <>
      <OnboardingContainer />
      <SuspenseLayout />
    </>
  );
}

// Define the router configuration outside of the App component
const router = createBrowserRouter([
  {
    element: <AppInitializer />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          { path: routes.CONVERSATIONS.pathname, element: <Conversations /> },
          { path: routes.OVERVIEW.pathname, element: <Overview /> },
          {
            path: routes.RECORDS.pathname,
            element: (
              <FeatureRoute feature='call_recording'>
                <Records />
              </FeatureRoute>
            )
          },
          { path: routes.TICKETS.pathname, element: <Tickets /> },
          {
            path: routes.CALL_REQUESTS.pathname,
            element: (
              <FeatureRoute feature={['callback_request', 'calendar_scheduling']} allRequired={false}>
                <Calendar />
              </FeatureRoute>
            )
          },
          {
            path: routes.REPORTS.pathname,
            element: (
              <FeatureRoute feature='analytics_reporting'>
                <Reports />
              </FeatureRoute>
            )
          },
          { path: routes.AGENTS.pathname, element: <Agents /> },
          { path: routes.SETTINGS.pathname, element: <Settings /> },
          {
            path: routes.LOGS.pathname,
            element: (
              <FeatureRoute feature='analytics_reporting'>
                <Logs />
              </FeatureRoute>
            )
          },
          { path: routes.TEAM.pathname, element: <Team /> },
          {
            path: routes.RECORD.pathname,
            element: (
              <FeatureRoute feature='call_recording'>
                <Record />
              </FeatureRoute>
            )
          },
          {
            path: routes.PROFILE.pathname,
            element: <Profile />,
            handle: { noHeader: true }
          },
          {
            path: routes.SCHEDULER.pathname,
            element: (
              <FeatureRoute feature='calendar_scheduling'>
                <Scheduler />
              </FeatureRoute>
            )
          },
          { path: routes.TWOFA.pathname, element: <TwoFa /> },
          {
            path: routes.DOCUMENTS.pathname,
            element: (
              <FeatureRoute feature='document_verification'>
                <DocumentVerification />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <Esignature />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE_UPLOAD.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <DocumentUploadPage />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE_BUILD.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <DocumentBuild />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE_TEMPLATES.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <EsignatureTemplates />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE_TEMPLATE_UPLOAD.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <TemplateUploadPage />
              </FeatureRoute>
            )
          },
          {
            path: routes.ESIGNATURE_TEMPLATE_BUILD.pathname,
            element: (
              <FeatureRoute feature='esignature'>
                <DocumentBuild isTemplate />
              </FeatureRoute>
            )
          }
        ]
      },
      {
        element: <AuthLayout />,
        children: [
          { path: routes.SETUP.pathname, element: <Setup /> },
          { path: routes.LOGIN.pathname, element: <Login /> },
          { path: routes.FORGOT_PASSWORD.pathname, element: <ForgotPassword /> },
          { path: routes.RESET_PASSWORD.pathname, element: <ResetPassword /> },
          { path: '/blank', element: <Fragment /> },
          { path: routes.FORBIDDEN.pathname, element: <Forbidden /> }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <Navigate to={routes.OVERVIEW.pathname} replace />
  }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default memo(withProfiler(App));
